import { useSelector } from "react-redux"
import { getSpaces } from "~/store"

import "./CalendarSpacesCard.scss"
import { Space } from "~/types"
import { useEffect, useRef, useState } from "react"
import axios from "axios"
import { deserialize } from "jsonapi-deserializer"
import IconCalendar from "../Icons/IconCalendar"
import classNames from "classnames"
import CalendarEventItem from "~/components/CalendarEventItem/CalendarEventItem"

export type CalendarSpace = {
  id: string
  name: string
  calendar_event?: {
    id: string
    title: string
    start_time: string
    end_time: string
  }
}

export type ExtendedCalendarSpace = CalendarSpace & {
  calendar_event: NonNullable<CalendarSpace["calendar_event"]>
  current_timer: Space["current_timer"]
  users: Space["users"]
  guests: Space["guests"]
}

const fetchSpaces = async () => {
  const response = await axios.get("/api/google_calendar_spaces")
  return deserialize(response.data) as CalendarSpace[]
}

const CalendarSpacesCard = () => {
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [scrollPosition, setScrollPosition] = useState<{
    atTop: boolean
    atBottom: boolean
  }>({ atTop: true, atBottom: false })

  const scrollAreaClassName = classNames("CalendarSpacesCard__scroll-area", {
    "CalendarSpacesCard__scroll-area--top": scrollPosition.atTop,
    "CalendarSpacesCard__scroll-area--bottom": scrollPosition.atBottom,
  })

  const [{ isLoading, spaces: calendarSpaces }, setSpacesState] = useState<
    | { isLoading: true; spaces: null }
    | {
        spaces: CalendarSpace[]
        isLoading: false
      }
  >({ isLoading: true, spaces: null })

  useEffect(() => {
    setSpacesState({ isLoading: true, spaces: null })
    fetchSpaces().then((spaces) => {
      setSpacesState({ spaces, isLoading: false })
    })
  }, [])

  const spaces = useSelector(getSpaces)
  const extendedSpaces = (calendarSpaces || [])
    .filter(
      (
        calendarSpace,
      ): calendarSpace is CalendarSpace & {
        calendar_event: NonNullable<CalendarSpace["calendar_event"]>
      } => calendarSpace.calendar_event != null,
    )
    .map((calendarSpace) => {
      const space = spaces.find((s) => s.id === calendarSpace.id)
      return {
        ...calendarSpace,
        current_timer: space?.current_timer,
        users: space?.users ?? [],
        guests: space?.guests ?? [],
      }
    })
    .sort((a, b) =>
      a.calendar_event.start_time.localeCompare(b.calendar_event.start_time),
    )

  useEffect(() => {
    const scrollArea = scrollAreaRef.current
    if (!scrollArea) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollArea
      setScrollPosition({
        atTop: scrollTop === 0,
        atBottom: scrollTop + clientHeight >= scrollHeight,
      })
    }

    handleScroll()
    scrollArea.addEventListener("scroll", handleScroll, { passive: true })
    return () => scrollArea.removeEventListener("scroll", handleScroll)
  }, [extendedSpaces.length])

  if (!isLoading && extendedSpaces.length == 0) {
    return (
      <div className="CalendarSpacesCard__empty">
        <div className="CalendarSpacesCard__empty-icon">
          <IconCalendar />
        </div>

        <div className="CalendarSpacesCard__empty-title">
          <div>No meetings</div>
          <div>today</div>
        </div>
      </div>
    )
  }

  return (
    <div className="CalendarSpacesCard">
      <div className={scrollAreaClassName} ref={scrollAreaRef}>
        {extendedSpaces.map((space) => (
          <CalendarEventItem key={space.id} space={space} />
        ))}
      </div>

      <div className="CalendarSpacesCard__overlay">
        <div className="CalendarSpacesCard__overlay-icon">
          <IconCalendar />
        </div>

        <div className="CalendarSpacesCard__overlay-title">
          <div>Join a</div>
          <div>meeting space</div>
        </div>
      </div>
    </div>
  )
}

export default CalendarSpacesCard
